/**
 * The country the visitor was last browsing (web plan MW6), for the pages that
 * have no `/{cc}/{lang}` prefix of their own: the account pages (`/favorites`,
 * `/notifications`, `/notification-settings`, `/profile`, `/settings`).
 *
 * A prefixed page's locale is always its URL's (master D-11); this is only the
 * answer for a global account page, so its links, its country selector and its
 * country-scoped queries (`favorites(country)`) stay on the market the visitor
 * came from instead of falling back to Pakistan. Written when a prefixed page
 * shows, read by `useRouteLocale()` through `useSyncExternalStore`, whose
 * server snapshot is "nothing remembered": the server and the first client
 * render agree, the remembered country applies right after hydration.
 *
 * Storage may be missing or throw (private windows, blocked site data): every
 * access is guarded and the answer is then "nothing remembered".
 */
import { parseLocale, localeKey, type Locale } from '@/seo/locales';

export const REMEMBERED_LOCALE_KEY = 'promostork.locale';

const listeners = new Set<() => void>();
/** Used when storage is unavailable, so the choice still holds for the tab's life. */
let fallback: string | null = null;

function read(): string | null {
  try {
    return window.localStorage.getItem(REMEMBERED_LOCALE_KEY) ?? fallback;
  } catch {
    return fallback;
  }
}

/** The remembered locale's key (`bh-en`) if it is a launched locale, else null. Stable between calls: a primitive. */
export function rememberedLocaleKey(): string | null {
  const value = read();
  if (!value) return null;
  const [cc, lang] = value.split('-');
  const locale = parseLocale(cc, lang);
  return locale ? localeKey(locale) : null;
}

export function rememberedLocale(): Locale | null {
  const key = rememberedLocaleKey();
  if (!key) return null;
  const [cc, lang] = key.split('-');
  return parseLocale(cc, lang);
}

/** Remembers `locale` as the one the visitor is browsing. */
export function rememberLocale(locale: Locale): void {
  const key = localeKey(locale);
  if (read() === key) return;
  fallback = key;
  try {
    window.localStorage.setItem(REMEMBERED_LOCALE_KEY, key);
  } catch {
    // Storage unavailable: `fallback` holds it.
  }
  listeners.forEach((listener) => listener());
}

/** `useSyncExternalStore`'s subscribe: this tab's writes and other tabs' (the `storage` event). */
export function subscribeRememberedLocale(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === REMEMBERED_LOCALE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** Server snapshot: nothing is remembered on the server. */
export const serverRememberedLocaleKey = (): null => null;

/** Test hook: forget everything (the in-memory fallback too). */
export function resetRememberedLocale(): void {
  fallback = null;
  try {
    window.localStorage.removeItem(REMEMBERED_LOCALE_KEY);
  } catch {
    // ignore
  }
  listeners.forEach((listener) => listener());
}
